/**
 * Distance calculation utilities for GPS coordinates
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'

export const EARTH_RADIUS_M = 6_371_000

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula
 * @param lat1 Latitude of first point in decimal degrees
 * @param lon1 Longitude of first point in decimal degrees
 * @param lat2 Latitude of second point in decimal degrees
 * @param lon2 Longitude of second point in decimal degrees
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const φ1 = (lat1 * Math.PI) / 180
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_M * c
}

/**
 * Calculates distance between two telemetry points
 */
export function calculatePointDistance(
  point1: GpsPoint,
  point2: GpsPoint
): number {
  return calculateDistance(
    point1.latitude,
    point1.longitude,
    point2.latitude,
    point2.longitude
  )
}

/**
 * Rounds distance to nearest interval (for indexing)
 * @param distance Distance in meters
 * @param interval Rounding interval in meters (default 5)
 * @returns Rounded distance
 */
export function roundDistance(distance: number, interval: number = 5): number {
  return Math.round(distance / interval) * interval
}

/**
 * Nearest item to `targetDistance` by a caller-supplied distance selector —
 * full linear scan, first-minimal wins on ties. The one nearest-point snap
 * shared by both clients' zone markers (web recalibrated points, mobile GPS
 * points), so tie-breaking can never drift between them.
 * @returns The closest item, or undefined for an empty array
 */
export function findClosestByDistance<T>(
  items: readonly T[],
  targetDistance: number,
  getDistance: (item: T) => number
): T | undefined {
  let closest: T | undefined
  let bestDiff = Infinity
  for (const item of items) {
    const diff = Math.abs(getDistance(item) - targetDistance)
    if (diff < bestDiff) {
      bestDiff = diff
      closest = item
    }
  }
  return closest
}

/**
 * Distance along the reference lap of the target's foot on the reference
 * polyline: the target is projected onto whichever of the two segments
 * meeting at the nearest fix `index` it lies closest to, and the reference
 * distance is interpolated along that segment (clamped to its ends). A lap
 * sampled denser than the reference lands between the reference fixes
 * instead of on them; a target beyond the last fix lands on it.
 */
function distanceAlongReference(
  tLat: number,
  tLon: number,
  index: number,
  refLats: number[],
  refLons: number[],
  refDistances: number[]
): number {
  const cosLat = Math.cos(tLat * 0.01745329252)
  const ax = (refLons[index] - tLon) * cosLat
  const ay = refLats[index] - tLat
  let best = refDistances[index]
  let bestPerpSq = Infinity
  const consider = (neighbour: number) => {
    if (neighbour < 0 || neighbour >= refLats.length) return
    const sx = (refLons[neighbour] - tLon) * cosLat - ax
    const sy = refLats[neighbour] - tLat - ay
    const len2 = sx * sx + sy * sy
    if (len2 === 0) return
    const u = Math.min(1, Math.max(0, -(ax * sx + ay * sy) / len2))
    const fx = ax + u * sx
    const fy = ay + u * sy
    const perpSq = fx * fx + fy * fy
    if (perpSq < bestPerpSq) {
      bestPerpSq = perpSq
      best =
        refDistances[index] +
        u * (refDistances[neighbour] - refDistances[index])
    }
  }
  consider(index - 1)
  consider(index + 1)
  return best
}

/**
 * ULTRA-OPTIMIZED: Batch recalibrate using raw coordinate arrays.
 * Avoids all object property access overhead by working with flat arrays.
 *
 * @param targetLats - Array of target latitudes
 * @param targetLons - Array of target longitudes
 * @param refLats - Array of reference latitudes
 * @param refLons - Array of reference longitudes
 * @param refDistances - Array of reference distances
 * @returns Array of recalibrated distances
 */
export function batchRecalibrateRaw(
  targetLats: number[],
  targetLons: number[],
  refLats: number[],
  refLons: number[],
  refDistances: number[]
): number[] {
  const targetLen = targetLats.length
  const refLen = refLats.length

  if (refLen === 0) {
    return new Array(targetLen).fill(0)
  }

  const result = new Array<number>(targetLen)
  let lastIndex = 0
  const searchWindow = 100
  // A physically close reference point can belong to a different part of the
  // track (the other leg of a hairpin), and matching it makes recalibrated
  // distances jump by the along-track separation of the legs. Candidates are
  // therefore restricted to a corridor around the expected progress (previous
  // match + GPS step); the corridor is wide enough for GPS noise and line
  // choice but far smaller than any leg-to-leg distance.
  const corridorBehindM = 50
  const corridorAheadM = 15
  const degToMeters = 111320
  // Corridor anchor: the last UNCLAMPED matched distance. Anchoring on the
  // monotonically clamped output instead would freeze the corridor after an
  // accepted forward mismatch and matching could never re-anchor.
  let anchor: number | undefined
  // Target distance travelled since `anchor` last moved. While a match is
  // stuck on the same reference point — e.g. the target is crossing a
  // reference GPS dropout whose first post-gap point lies beyond the fixed
  // forward corridor — this keeps growing so the expected-progress corridor
  // widens forward and re-acquires the reference, instead of freezing the
  // recalibrated distance (and flat-lining the chart) for the rest of the lap.
  let travelSinceAnchor = 0

  // PERF: Reference travel directions don't depend on the target point, so
  // they are precomputed once (O(refLen)) instead of per candidate in the
  // hot loop (the dot product against the target's step direction is all
  // that remains there).
  const refDirLonM = new Array<number>(refLen)
  const refDirLatM = new Array<number>(refLen)
  for (let i = 0; i < refLen; i++) {
    const next = i + 1 < refLen ? i + 1 : i
    const prev = next === i ? Math.max(0, i - 1) : i
    refDirLonM[i] =
      (refLons[next] - refLons[prev]) *
      Math.cos((refLats[next] + refLats[prev]) * 0.00872664626)
    refDirLatM[i] = refLats[next] - refLats[prev]
  }

  for (let t = 0; t < targetLen; t++) {
    const tLat = targetLats[t]
    const tLon = targetLons[t]

    // Sliding window search
    const start = Math.max(0, lastIndex - 20)
    const end = Math.min(refLen, start + searchWindow)

    // Expected along-track progress for the corridor check (skipped for the
    // very first point, which has no anchor yet)
    let minRefDist = -Infinity
    let maxRefDist = Infinity
    // Target travel direction, for rejecting candidates on opposite-direction
    // track sections (e.g. the other leg of a hairpin, which can be the
    // geometrically NEAREST line when the riders' line choices differ)
    let stepLonM = 0
    let stepLatM = 0
    let hasHeading = false
    if (t > 0 && anchor !== undefined) {
      stepLonM =
        (tLon - targetLons[t - 1]) *
        Math.cos((tLat + targetLats[t - 1]) * 0.00872664626)
      stepLatM = tLat - targetLats[t - 1]
      const stepMeters =
        Math.sqrt(stepLonM * stepLonM + stepLatM * stepLatM) * degToMeters
      // Below ~0.5m of movement, GPS noise dominates the heading
      hasHeading = stepMeters > 0.5
      travelSinceAnchor += stepMeters
      // Expected progress measures from the anchor by how far the TARGET has
      // travelled since the anchor last moved (one step in the normal case),
      // so a stuck match widens the corridor forward each point.
      const expected = anchor + travelSinceAnchor
      minRefDist = expected - corridorBehindM
      maxRefDist = expected + corridorAheadM
    }

    let closestIndex = -1
    let smallestDistSq = Infinity

    for (let i = start; i < end; i++) {
      // PERF: The corridor and heading checks run BEFORE the squared-distance
      // math — they are cheap compares and reject most of the window, so the
      // common path does less work per candidate than an unconditional
      // distance computation would.
      const refDist = refDistances[i]
      if (refDist < minRefDist || refDist > maxRefDist) continue

      // Reject candidates whose local track direction opposes the target's
      // travel direction (same scaling on both vectors keeps the dot
      // product's sign meaningful)
      if (
        hasHeading &&
        refDirLonM[i] * stepLonM + refDirLatM[i] * stepLatM < 0
      ) {
        continue
      }

      // Inline fast squared distance calculation
      const dx =
        (refLons[i] - tLon) * Math.cos((tLat + refLats[i]) * 0.00872664626)
      const dy = refLats[i] - tLat
      const distSq = dx * dx + dy * dy

      if (distSq < smallestDistSq) {
        smallestDistSq = distSq
        closestIndex = i
        if (distSq < 1e-10) break
      }
    }

    if (closestIndex === -1) {
      // Nothing inside the corridor (reference data gap or a lost match) —
      // rare recovery pass: fall back to the geometric nearest in the window
      // so matching can re-anchor.
      let nearestAnyIndex = start
      let nearestAnyDistSq = Infinity
      for (let i = start; i < end; i++) {
        const dx =
          (refLons[i] - tLon) * Math.cos((tLat + refLats[i]) * 0.00872664626)
        const dy = refLats[i] - tLat
        const distSq = dx * dx + dy * dy
        if (distSq < nearestAnyDistSq) {
          nearestAnyDistSq = distSq
          nearestAnyIndex = i
        }
      }
      anchor = refDistances[nearestAnyIndex]
      travelSinceAnchor = 0
      result[t] = distanceAlongReference(
        tLat,
        tLon,
        nearestAnyIndex,
        refLats,
        refLons,
        refDistances
      )
      lastIndex = nearestAnyIndex
    } else {
      // Clamp residual backward jitter (e.g. crossing the apex, where the
      // legs genuinely converge) so chart distances stay monotonic; large
      // jumps were already rejected by the corridor.
      const matched = refDistances[closestIndex]
      // A fresh match (it moved off the previous reference point) resets the
      // accumulator; staying on the exact same reference distance keeps it
      // growing so the corridor can widen forward across a reference gap.
      if (anchor === undefined || matched !== anchor) {
        travelSinceAnchor = 0
      }
      anchor = matched
      const along = distanceAlongReference(
        tLat,
        tLon,
        closestIndex,
        refLats,
        refLons,
        refDistances
      )
      result[t] = t > 0 && along < result[t - 1] ? result[t - 1] : along
      lastIndex = closestIndex
    }
  }

  return result
}
